import {
  OvertimeLegalReasonEnum,
  usePortalCompensationDecideMutation,
  usePortalDeviceDecideMutation,
  usePortalEmployeeProfileSaveMutation,
  usePortalOvertimeDecideMutation,
  usePortalPunchClaimDecideMutation,
  usePortalShiftSwapDecideMutation,
  usePortalStayOppositionMutation,
  usePortalTeamQuery,
} from "@dashboard/graphql";
import { Box, Button, Input, Option, Select, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { AbsenceList } from "../AbsenceList";
import { BalanceView } from "../BalanceView";
import { ErrorText, InlineText, Page, Section } from "../components";
import { portalLabels as l } from "../labels";
import { portalMessages as m } from "../messages";
import { LegalReasonSelect } from "../OvertimeParts";
import { employeeUrl, portalPaths } from "../urls";
import { formatDateTime, formatDay, formatMinutes, isoDay, today } from "../utils";
import { LivePins } from "./PinScreen";

const SCHEDULE_TYPE: Record<string, MessageDescriptor> = {
  FIXED: l.scheduleFixed,
  SHIFTS: l.scheduleShifts,
  EXEMPT: l.scheduleExempt,
};

const OppositionButton = ({
  userId,
  date,
  alertId,
  onDone,
}: {
  userId: string;
  date: string;
  alertId: string;
  onDone: () => void;
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [record, opts] = usePortalStayOppositionMutation({
    onCompleted: data => {
      if (!data.stayOppositionRecord?.errors.length) {
        setOpen(false);
        onDone();
      }
    },
  });

  return open ? (
    <Box width="100%">
      <InlineText
        defaultValue={intl.formatMessage(m.opposeDefault)}
        loading={opts.loading}
        onCancel={() => setOpen(false)}
        onSubmit={note => record({ variables: { userId, date, note, alertId } })}
      />
      <ErrorText errors={opts.data?.stayOppositionRecord?.errors} />
    </Box>
  ) : (
    <Button size="small" variant="secondary" onClick={() => setOpen(true)}>
      {intl.formatMessage(m.oppose)}
    </Button>
  );
};

const OvertimeDecisionRow = ({
  request,
  onDone,
}: {
  request: { id: string; employeeName: string; date: string; minutes: number; reason: string };
  onDone: () => void;
}) => {
  const intl = useIntl();
  const [legalReason, setLegalReason] = useState<OvertimeLegalReasonEnum | "">("");
  const [decide, opts] = usePortalOvertimeDecideMutation({
    onCompleted: result => {
      if (!result.overtimeRequestDecide?.errors.length) {
        onDone();
      }
    },
  });

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <Text size={2}>
          <b>{request.employeeName}</b> · {formatDay(request.date)} ·{" "}
          {formatMinutes(request.minutes)} · {request.reason}
        </Text>
        <Box __minWidth="260px">
          <LegalReasonSelect value={legalReason} onChange={setLegalReason} />
        </Box>
        <Button
          size="small"
          variant="primary"
          disabled={!legalReason || opts.loading}
          onClick={() =>
            decide({
              variables: {
                id: request.id,
                approved: true,
                legalReason: legalReason || undefined,
              },
            })
          }
        >
          {intl.formatMessage(m.accept)}
        </Button>
        <Button
          size="small"
          variant="secondary"
          disabled={opts.loading}
          onClick={() => decide({ variables: { id: request.id, approved: false } })}
        >
          {intl.formatMessage(m.refuse)}
        </Button>
      </Box>
      <ErrorText errors={opts.data?.overtimeRequestDecide?.errors} />
    </Box>
  );
};

export const Team = () => {
  const intl = useIntl();
  const monthStart = `${today().slice(0, 7)}-01`;
  const { data, refetch } = usePortalTeamQuery({
    variables: { start: monthStart, end: today() },
    fetchPolicy: "cache-and-network",
  });
  const [decideClaim] = usePortalPunchClaimDecideMutation({ onCompleted: () => refetch() });
  const [decideComp] = usePortalCompensationDecideMutation({ onCompleted: () => refetch() });
  const [decideSwap] = usePortalShiftSwapDecideMutation({ onCompleted: () => refetch() });
  const [decideDevice] = usePortalDeviceDecideMutation({ onCompleted: () => refetch() });
  const [newProfile, setNewProfile] = useState({ userId: "", fullName: "", hireDate: today() });
  const [createProfile, createOpts] = usePortalEmployeeProfileSaveMutation({
    onCompleted: result => {
      if (!result.employeeProfileSave?.errors.length) {
        setNewProfile({ userId: "", fullName: "", hireDate: today() });
        refetch();
      }
    },
  });
  const staffOptions: Option[] = (data?.staffWithoutProfile ?? []).map(staff => ({
    value: staff.userId,
    label: `${staff.name} (${staff.email})`,
  }));

  return (
    <Page>
      <Section
        title={intl.formatMessage(m.todaysPins)}
        actions={
          <Link to={portalPaths.pinScreen} target="_blank">
            <Button size="small" variant="secondary" as="span">
              {intl.formatMessage(m.pinFullScreen)}
            </Button>
          </Link>
        }
      >
        <LivePins />
        <Text size={1} color="default2">
          {intl.formatMessage(m.pinHint)}
        </Text>
      </Section>

      {(data?.personalDevices.length ?? 0) > 0 && (
        <Section title={intl.formatMessage(m.newDevices)}>
          {data?.personalDevices.map(device => (
            <Box key={device.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Text size={2}>
                <b>{device.employeeName}</b> · {device.label || "—"} ·{" "}
                {formatDateTime(device.createdAt)}
              </Text>
              <Button
                size="small"
                variant="primary"
                onClick={() => decideDevice({ variables: { id: device.id, approve: true } })}
              >
                {intl.formatMessage(m.accept)}
              </Button>
              <Button
                size="small"
                variant="secondary"
                onClick={() => decideDevice({ variables: { id: device.id, approve: false } })}
              >
                {intl.formatMessage(m.refuse)}
              </Button>
            </Box>
          ))}
          <Text size={1} color="default2">
            {intl.formatMessage(m.newDevicesHint)}
          </Text>
        </Section>
      )}

      {(data?.punchClaims.length ?? 0) > 0 && (
        <Section title={intl.formatMessage(m.claimsTitle)}>
          {data?.punchClaims.map(item => (
            <Box key={item.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Text size={2}>
                <b>{item.employeeName}</b> ·{" "}
                {intl.formatMessage(m.claimDecideLine, {
                  recorded: formatDateTime(item.recordedAt),
                  claimed: formatDateTime(item.claimedAt),
                })}{" "}
                — {item.reason}
              </Text>
              <Button
                size="small"
                variant="primary"
                onClick={() => decideClaim({ variables: { id: item.id, approve: true } })}
              >
                {intl.formatMessage(m.accept)}
              </Button>
              <Button
                size="small"
                variant="secondary"
                onClick={() => decideClaim({ variables: { id: item.id, approve: false } })}
              >
                {intl.formatMessage(m.refuse)}
              </Button>
            </Box>
          ))}
        </Section>
      )}

      {(data?.compensationRequests.length ?? 0) > 0 && (
        <Section title={intl.formatMessage(m.compensationTitle)}>
          {data?.compensationRequests.map(item => (
            <Box key={item.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Text size={2}>
                <b>{item.employeeName}</b> · {formatDay(item.absenceDate)} →{" "}
                {formatDay(item.compensationDate)} · {formatMinutes(item.minutes)} — {item.reason}
              </Text>
              <Button
                size="small"
                variant="primary"
                onClick={() => decideComp({ variables: { id: item.id, approve: true } })}
              >
                {intl.formatMessage(m.accept)}
              </Button>
              <Button
                size="small"
                variant="secondary"
                onClick={() => decideComp({ variables: { id: item.id, approve: false } })}
              >
                {intl.formatMessage(m.refuse)}
              </Button>
            </Box>
          ))}
        </Section>
      )}

      <Section title={intl.formatMessage(m.balanceReport)}>
        {(data?.balanceReport ?? []).map(balance => (
          <Box
            key={balance.userId}
            display="flex"
            flexDirection="column"
            gap={2}
            paddingY={2}
            borderBottomWidth={1}
            borderBottomStyle="solid"
            borderColor="default1"
          >
            <Text size={3} fontWeight="bold">
              {balance.employeeName} ·{" "}
              {intl.formatMessage(m.punctualityDays, { count: balance.punctualityDays })}
            </Text>
            <BalanceView balance={balance} />
          </Box>
        ))}
      </Section>

      <Section title={intl.formatMessage(m.absenceRequests)}>
        {(data?.absences ?? []).length === 0 && (
          <Text size={2} color="default2">
            {intl.formatMessage(m.noAlerts)}
          </Text>
        )}
        <AbsenceList absences={data?.absences ?? []} manager showName onChanged={refetch} />
      </Section>

      <Section title={intl.formatMessage(m.alerts)}>
        {(data?.timekeepingAlerts ?? []).length === 0 && (
          <Text size={2} color="default2">
            {intl.formatMessage(m.noAlerts)}
          </Text>
        )}
        {data?.timekeepingAlerts.map((alert, index) => {
          const title = (l as Record<string, MessageDescriptor>)[`alert_${alert.code}`];

          return (
            <Text key={index} size={2}>
              <b>{alert.employeeName}</b>
              {alert.date ? ` · ${formatDay(alert.date)}` : ""} —{" "}
              {intl.locale.startsWith("pt") || !title ? alert.message : intl.formatMessage(title)}
            </Text>
          );
        })}
      </Section>

      <Section title={intl.formatMessage(m.overstays)}>
        <Text size={2} color="default2">
          {intl.formatMessage(m.realWorkHint)}
        </Text>
        {(data?.overstayAlerts ?? []).map(alert => (
          <Box key={alert.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Text size={2}>
              <b>{alert.employeeName}</b> ·{" "}
              {intl.formatMessage(l.overstayLine, {
                end: formatDateTime(alert.plannedEnd),
                alerted: formatDateTime(alert.alertedAt),
              })}
            </Text>
            {alert.opposed ? (
              <Text size={2} color="success1">
                {intl.formatMessage(m.opposed)}
              </Text>
            ) : (
              <OppositionButton
                userId={alert.userId}
                date={isoDay(new Date(alert.plannedEnd))}
                alertId={alert.id}
                onDone={refetch}
              />
            )}
          </Box>
        ))}
      </Section>

      <Section title={intl.formatMessage(m.overtimeRequests)}>
        {(data?.overtimeRequests ?? []).map(request => (
          <OvertimeDecisionRow key={request.id} request={request} onDone={refetch} />
        ))}
      </Section>

      <Section title={intl.formatMessage(m.swapsToDecide)}>
        {(data?.shiftSwaps ?? []).map(swap => (
          <Box key={swap.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Text size={2}>
              {swap.requesterName} ({formatDay(swap.requesterDate)}) ⇄ {swap.colleagueName} (
              {formatDay(swap.colleagueDate)}) {swap.reason && `— ${swap.reason}`}
            </Text>
            <Button
              size="small"
              variant="primary"
              onClick={() => decideSwap({ variables: { id: swap.id, approve: true } })}
            >
              {intl.formatMessage(m.accept)}
            </Button>
            <Button
              size="small"
              variant="secondary"
              onClick={() => decideSwap({ variables: { id: swap.id, approve: false } })}
            >
              {intl.formatMessage(m.refuse)}
            </Button>
          </Box>
        ))}
      </Section>

      <Section title={intl.formatMessage(m.employees)}>
        {(data?.employeeProfiles ?? []).map(employee => (
          <Box key={employee.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Text size={3} fontWeight="bold" __minWidth="200px">
              {employee.fullName}
            </Text>
            <Text size={2} color="default2">
              {employee.roleNames.join(", ") || employee.jobTitle || "—"} · {employee.weeklyHours} h
              · {intl.formatMessage(SCHEDULE_TYPE[employee.scheduleType])}
              {employee.terminationDate ? ` · ${intl.formatMessage(l.terminated)}` : ""}
            </Text>
            <Box __flex={1} />
            <Link to={employeeUrl(employee.userId)}>
              <Button size="small" variant="secondary" as="span">
                {intl.formatMessage(m.open)}
              </Button>
            </Link>
          </Box>
        ))}
        <Text size={4} fontWeight="bold">
          {intl.formatMessage(m.newEmployee)}
        </Text>
        <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))">
          <Select
            size="small"
            label={intl.formatMessage(m.staffUserId)}
            options={staffOptions}
            value={staffOptions.find(o => o.value === newProfile.userId) ?? null}
            onChange={option => setNewProfile({ ...newProfile, userId: option.value })}
          />
          <Input
            size="small"
            label={intl.formatMessage(m.name)}
            value={newProfile.fullName}
            maxLength={256}
            onChange={event => setNewProfile({ ...newProfile, fullName: event.target.value })}
          />
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(l.hireDate)}
            value={newProfile.hireDate}
            onChange={event => setNewProfile({ ...newProfile, hireDate: event.target.value })}
          />
        </Box>
        <Box>
          <Button
            size="small"
            variant="primary"
            disabled={!newProfile.userId || !newProfile.fullName.trim() || createOpts.loading}
            onClick={() =>
              createProfile({
                variables: {
                  userId: newProfile.userId,
                  input: { fullName: newProfile.fullName.trim(), hireDate: newProfile.hireDate },
                },
              })
            }
          >
            {intl.formatMessage(m.save)}
          </Button>
        </Box>
        <ErrorText errors={createOpts.data?.employeeProfileSave?.errors} />
      </Section>
    </Page>
  );
};
